"use client";

import { cn } from "@/lib/utils";
import { useId } from "react";

export type LogoFestiveTheme = "trung-thu" | "womens-day" | "teachers-day";

type Props = {
  showText?: boolean;
  size?: "sm" | "md";
  variant?: "default" | "onDark";
  /** @deprecated dùng festiveTheme */
  festive?: boolean;
  festiveTheme?: LogoFestiveTheme | null;
  className?: string;
};

function LogoMark({
  size,
  gradId,
  theme,
}: {
  size: number;
  gradId: string;
  theme?: LogoFestiveTheme | null;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className="drop-shadow-sm"
    >
      <defs>
        <linearGradient id={gradId} x1="6" y1="4" x2="42" y2="44" gradientUnits="userSpaceOnUse">
          {theme === "womens-day" ? (
            <>
              <stop stopColor="#fb7185" />
              <stop stopColor="#f43f5e" />
              <stop stopColor="#be123c" />
              <stop offset="1" stopColor="#fda4af" />
            </>
          ) : theme === "teachers-day" ? (
            <>
              <stop stopColor="#34d399" />
              <stop stopColor="#059669" />
              <stop stopColor="#047857" />
              <stop offset="1" stopColor="#fb7185" />
            </>
          ) : theme === "trung-thu" ? (
            <>
              <stop stopColor="#fbbf24" />
              <stop stopColor="#f97316" />
              <stop stopColor="#ef4444" />
              <stop offset="1" stopColor="#fde68a" />
            </>
          ) : (
            <>
              <stop stopColor="#38bdf8" />
              <stop stopColor="#a78bfa" />
              <stop stopColor="#fb7185" />
              <stop offset="1" stopColor="#fbbf24" />
            </>
          )}
        </linearGradient>
      </defs>
      <rect x="3" y="3" width="42" height="42" rx="14" fill={`url(#${gradId})`} />
      <rect x="6" y="6" width="36" height="36" rx="11" fill="white" fillOpacity="0.22" />
      <circle cx="14" cy="13" r="2.5" fill="white" fillOpacity="0.85" />
      <circle
        cx="34"
        cy="11"
        r="1.8"
        fill={theme === "womens-day" ? "#fecdd3" : theme === "teachers-day" ? "#fecdd3" : "#fef08a"}
      />
      <circle cx="37" cy="34" r="2" fill="white" fillOpacity="0.7" />
      <path
        d="M16 31.5c0-5.2 3.2-8.8 8-8.8s8 3.6 8 8.8"
        stroke="white"
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="none"
        opacity="0.35"
      />
      <text
        x="24"
        y="29"
        textAnchor="middle"
        fill="white"
        fontSize="15"
        fontWeight="900"
        fontFamily="ui-rounded, 'Segoe UI Rounded', 'Arial Rounded MT Bold', system-ui, sans-serif"
        style={{ letterSpacing: "-0.04em" }}
      >
        TS
      </text>
    </svg>
  );
}

export function TsOriginLogo({
  showText = true,
  size = "md",
  variant = "default",
  festive = false,
  festiveTheme = null,
  className,
}: Props) {
  const gradId = useId().replace(/:/g, "");
  const iconSize = size === "sm" ? 36 : 44;
  const onDark = variant === "onDark";
  const theme: LogoFestiveTheme | null = festiveTheme ?? (festive ? "trung-thu" : null);

  return (
    <div className={cn("flex items-center gap-2.5", className)}>
      <div className="relative shrink-0 transition-transform duration-300 hover:scale-105 hover:-rotate-2">
        <LogoMark size={iconSize} gradId={gradId} theme={theme} />
        {theme === "trung-thu" && (
          <span className="absolute -top-1.5 -left-1 text-sm drop-shadow-md" aria-hidden>
            🌕
          </span>
        )}
        {theme === "womens-day" && (
          <svg
            className="absolute -top-1.5 -left-1 h-4 w-4 text-rose-300 drop-shadow-md"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden
          >
            <path d="M12 3c2 3.5 1.5 6.5-.5 8.5 3-.5 5.5 1.5 6.5 4.5-2.5-1-5.5.5-7 3 .5-3-1-6-4-7.5 2.5.2 4-3 5-8.5z" />
          </svg>
        )}
        {theme === "teachers-day" && (
          <svg
            className="absolute -top-1.5 -left-1 h-4 w-4 text-rose-300 drop-shadow-md"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden
          >
            <path d="M12 3c2 3.5 1.5 6.5-.5 8.5 3-.5 5.5 1.5 6.5 4.5-2.5-1-5.5.5-7 3 .5-3-1-6-4-7.5 2.5.2 4-3 5-8.5z" />
          </svg>
        )}
        <span
          className={cn(
            "absolute -bottom-1 -right-1.5 rotate-[-8deg] rounded-lg border-2 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide shadow-md",
            theme === "womens-day"
              ? "border-rose-100 bg-rose-600 text-white"
              : theme === "teachers-day"
                ? "border-emerald-100 bg-emerald-600 text-white"
                : theme === "trung-thu"
                  ? "border-amber-100 bg-rose-500 text-white"
                  : onDark
                    ? "border-white/80 bg-lime-300 text-lime-950"
                    : "border-white bg-lime-400 text-lime-950 dark:border-slate-900",
          )}
        >
          {theme === "womens-day" ? "20" : theme === "teachers-day" ? "20" : theme === "trung-thu" ? "TT" : "PC"}
        </span>
      </div>

      {showText ? (
        <div className="min-w-0 leading-none">
          <p
            className={cn(
              "bg-clip-text font-black tracking-tight text-transparent",
              size === "sm" ? "text-base" : "text-xl",
              theme === "womens-day"
                ? "bg-gradient-to-r from-rose-200 via-pink-200 to-rose-300"
                : theme === "teachers-day"
                  ? "bg-gradient-to-r from-emerald-200 via-teal-100 to-rose-200"
                  : theme === "trung-thu"
                    ? "bg-gradient-to-r from-amber-200 via-orange-300 to-rose-300"
                    : onDark
                      ? "bg-gradient-to-r from-sky-100 via-fuchsia-100 to-amber-100"
                      : "bg-gradient-to-r from-sky-500 via-fuchsia-500 to-amber-500",
            )}
            style={{ fontFamily: "ui-rounded, 'Segoe UI Rounded', system-ui, sans-serif" }}
          >
            TS Origin PC
          </p>
          <p
            className={cn(
              "mt-1 text-[10px] font-semibold uppercase tracking-[0.18em]",
              theme === "womens-day"
                ? "text-rose-300/95"
                : theme === "teachers-day"
                  ? "text-emerald-300/95"
                  : theme === "trung-thu"
                    ? "text-amber-300/95"
                    : onDark
                      ? "text-white/75"
                      : "text-amber-500/90",
            )}
          >
            {theme === "womens-day"
              ? "Ngày Phụ nữ VN"
              : theme === "teachers-day"
                ? "Nhà Giáo 20/11"
                : theme === "trung-thu"
                  ? "Mừng Trung Thu"
                  : "Chơi vui · Khám phá"}
          </p>
        </div>
      ) : null}
    </div>
  );
}
